import { useNavigate } from 'react-router-dom';
import PageNavbar from '../../../components/feature/PageNavbar';

export default function ExoskeletonPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <PageNavbar />

      {/* Hero Section */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-sm text-gray-500 uppercase tracking-wide">Wearable Robotic Device</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-3 mb-6">
                Lower Limb Exoskeleton System
              </h2>
              <p className="text-base text-gray-700 leading-relaxed mb-5">
                Our advanced exoskeleton technology provides powered assistance for lower limb rehabilitation and mobility training. This wearable robotic device supports patients with neurological and orthopedic conditions in regaining walking ability and improving gait patterns through intensive, repetitive training.
              </p>
              <p className="text-base text-gray-700 leading-relaxed">
                The system combines motorized joint assistance with real-time sensors to deliver personalized gait training that adapts to each patient's capabilities and progress.
              </p>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/a7bf65bd8b13f4da56f565b0ebf5ebe5.jpg"
                alt="Lower Limb Exoskeleton System"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Technology */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Core Technology</h3>
          
          <div className="space-y-8">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Motorized Joint Assistance</h4>
              <p className="text-gray-700 leading-relaxed">
                Powered motors at hip and knee joints provide precise assistance, enabling patients to practice walking movements even with significant weakness. The system delivers controlled torque that complements the patient's own effort, promoting active participation rather than passive movement.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Adjustable Support Levels</h4>
              <p className="text-gray-700 leading-relaxed">
                Therapists can customize the amount of assistance provided at each joint, gradually reducing support as patient strength and control improve. This progressive approach ensures optimal challenge throughout the recovery process.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Natural Gait Patterns</h4>
              <p className="text-gray-700 leading-relaxed">
                Advanced algorithms ensure movements closely mimic natural walking patterns, promoting proper motor learning and muscle activation. The system analyzes gait phases in real-time and adjusts assistance to maintain physiological movement patterns.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Real-time Sensor Integration</h4>
              <p className="text-gray-700 leading-relaxed">
                Integrated sensors continuously monitor patient movements, joint angles, and force distribution. This data enables the system to provide optimal assistance while ensuring safety, and allows therapists to track detailed performance metrics over time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-10">Technical Specifications</h3>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-6">Device Parameters</h4>
              <dl className="space-y-4">
                <div className="flex justify-between border-b border-gray-200 pb-3">
                  <dt className="text-gray-600">Weight Capacity</dt>
                  <dd className="font-medium text-gray-900">Up to 220 lbs (100 kg)</dd>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-3">
                  <dt className="text-gray-600">Height Range</dt>
                  <dd className="font-medium text-gray-900">5'0" - 6'4" (152-193 cm)</dd>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-3">
                  <dt className="text-gray-600">Battery Life</dt>
                  <dd className="font-medium text-gray-900">4-6 hours continuous use</dd>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-3">
                  <dt className="text-gray-600">Walking Speed</dt>
                  <dd className="font-medium text-gray-900">0.1 - 3.0 mph adjustable</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-600">Joint Assistance</dt>
                  <dd className="font-medium text-gray-900">Hip & Knee bilateral</dd>
                </div>
              </dl>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-6">Safety Features</h4>
              <ul className="space-y-3">
                <li className="text-gray-700">
                  <strong className="text-gray-900">Emergency Stop Function</strong> — Immediate halt capability for patient safety
                </li>
                <li className="text-gray-700">
                  <strong className="text-gray-900">Fall Prevention System</strong> — Integrated support harness prevents falls during training
                </li>
                <li className="text-gray-700">
                  <strong className="text-gray-900">Force Limitation</strong> — Prevents excessive joint forces that could cause injury
                </li>
                <li className="text-gray-700">
                  <strong className="text-gray-900">Stability Monitoring</strong> — Continuous balance assessment with automatic adjustments
                </li>
                <li className="text-gray-700">
                  <strong className="text-gray-900">Secure Attachment</strong> — Multiple safety straps and locks ensure device stays properly positioned
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Clinical Applications */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Suitable Patient Types</h3>
          
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Stroke Survivors</h4>
              <p className="text-sm text-gray-700">Helps patients with hemiplegia regain walking ability through intensive gait training</p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Spinal Cord Injury</h4>
              <p className="text-sm text-gray-700">Enables standing and walking practice for incomplete SCI patients</p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Traumatic Brain Injury</h4>
              <p className="text-sm text-gray-700">Supports mobility recovery with adjustable assistance levels</p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Multiple Sclerosis</h4>
              <p className="text-sm text-gray-700">Maintains and improves walking function with powered support</p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Post-surgical Recovery</h4>
              <p className="text-sm text-gray-700">Facilitates early mobilization after orthopedic surgeries</p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Parkinson's Disease</h4>
              <p className="text-sm text-gray-700">Improves gait patterns and reduces freezing episodes</p>
            </div>
          </div>
        </div>
      </section>

      {/* Training Approach */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Training Process</h3>
          
          <div className="space-y-6">
            <div className="border-l-2 border-gray-300 pl-6">
              <h4 className="font-semibold text-gray-900 mb-2">Fitting & Setup</h4>
              <p className="text-gray-700">Custom fitting and device calibration for patient body dimensions and capabilities</p>
            </div>

            <div className="border-l-2 border-gray-300 pl-6">
              <h4 className="font-semibold text-gray-900 mb-2">Initial Training</h4>
              <p className="text-gray-700">Learning to use the device with maximum support, focusing on basic gait patterns</p>
            </div>

            <div className="border-l-2 border-gray-300 pl-6">
              <h4 className="font-semibold text-gray-900 mb-2">Progressive Training</h4>
              <p className="text-gray-700">Gradually reducing assistance as ability improves, increasing challenge and independence</p>
            </div>

            <div className="border-l-2 border-gray-300 pl-6">
              <h4 className="font-semibold text-gray-900 mb-2">Advanced Practice</h4>
              <p className="text-gray-700">Complex walking tasks and varied environments to prepare for real-world mobility</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-8 px-6 border-t border-gray-100 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Accelerated Recovery Centre. All rights reserved.
          </p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="nofollow noopener noreferrer"
            className="text-gray-400 text-xs hover:text-gray-600 transition-colors mt-2 inline-block"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
